// src/modules/Inventory/RapportZonesDetails.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { secureGet } from "../../config/api";
import { useLocation } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";

const formatPercent = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const pct = v <= 1 ? v * 100 : v;
  return `${Math.round(pct)} %`;
};

const formatElapsed = (seconds) => {
  if (!seconds && seconds !== 0) return "—";
  const s = Math.max(0, Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h}h ${String(m).padStart(2, "0")}`;
};

const RapportZonesDetails = () => {
  const { t } = useTranslation();
  const { zone } = useLocation().state; 
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [locations, setLocations] = useState([]);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const pageRows = (data) => (Array.isArray(data) ? data : data?.content ?? []);

  const fetchDetails = useCallback(
    async (zoneId) => {
      setLoading(true);
      try {
        const locationsRes = await secureGet(
          `/inventory-locations?zoneId=${zoneId}`,
          showSnackbar,
          { signal }
        );
        const locationsList = pageRows(locationsRes?.data);

        const formatted = locationsList.map((loc, idx) => ({
          id: String(loc.id ?? idx),
          title:
            loc.name,
            status: t(`inventory_status.${loc.status}`),
          products: loc.totalProducts,
          elapsed: formatElapsed(loc.elapsedTime),
        }));

        setLocations(formatted);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Failed to fetch locations", e);
          setLocations([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [signal]
  );

  useEffect(() => {
    fetchDetails(zone.id);
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const headerChip = t(`inventory_status.${zone.status}`);

  const metrics = [
    { label: "AGENT DE COMPTAGE", value: zone.countedByName || "—" },
    { label: "AGENT DE CONTRÔLE", value: zone.controlledByName || "—" },
    { label: "TAUX D’AVANCEMENT", value: formatPercent(zone.progressRate) },
    {
      label: "EMPLACEMENTS TRAITÉS",
      value:
        zone.treatedLocations != null && zone.totalLocations != null
          ? `${zone.treatedLocations}/${zone.totalLocations}`
          : "—",
    },
    { label: "TEMPS ÉCOULÉ", value: formatElapsed(zone.elapsedTime) },
  ];

  const renderGrid = () => {
    if (!locations.length) {
      return <div className="text-gray-500 text-sm mt-6" />;
    }

    return (
      <div className="grid grid-cols-4 gap-4">
        {locations.map((r) => (
          <div
            key={r.id}
            className="bg-white rounded-[12px] shadow-[0px_20px_80px_#00000011] px-4 py-[16px]"
          >
            <div className="flex justify-between items-start mb-1.5">
              <div className="font-semibold text-[18px] text-[#38485C] leading-5">
                {r.title}
              </div>
              <span className="text-[10px] font-semibold bg-[#6A79F8] text-white px-2.5 py-1 rounded-[12px] whitespace-nowrap">
                {r.status}
              </span>
            </div>

            <div className="flex gap-7 mt-1.5">
              <div className="text-xs text-gray-500">
                <div className="font-medium text-[#38485C] text-[14px]">
                  PRODUITS {r.products ?? 0}
                </div>
              </div>
              <div className="text-xs text-gray-500 ml-auto">
                <div className="font-medium text-[#A8ADC3] text-[14px]">
                  TEMPS ÉCOULÉ {r.elapsed || "—"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>GrayFox - {t("titles.zone_details")}</title>
      </Helmet>

      <Breadcrumbs />

      {/* Title + status chip (matches the mock) */}
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-[40px] font-semibold text-black">
          {t("titles.zone_details")}
          <span className="ml-2 font-bold text-[#3771B0]">
            {zone?.name}
          </span>
        </h1>
        <span className="text-[14px] font-bold bg-[#A8ADC3] text-[#FFFFFF] px-3 py-2 rounded-[12px] h-6 inline-flex items-center">
          {headerChip.toUpperCase()}
        </span>
      </div>

      {/* Summary Header */}
      <div className="grid grid-cols-5 gap-4 bg-white rounded-[12px] shadow-[0px_20px_80px_#00000011] p-4 mb-[18px]">
        {metrics.map((m) => (
          <Metric key={m.label} label={m.label} value={m.value} />
        ))}
      </div>

      {/* Section title */}
      <div className="text-[22px] font-semibold text-[#00000] mb-3">Emplacements</div>

      {/* Grid */}
      <div className="relative min-h-[160px] mt-2">
        {loading && (
          <div className="absolute inset-0 bg-white/60 flex justify-center items-center z-[1]">
            <LoadingLogo />
          </div>
        )}
        {renderGrid()}
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div >
    <div className="text-[14px] font-semibold text-[#707070] tracking-wide">
      {label}
    </div>
    <div className="text-[20px] font-semibold text-[#38485C]">{value}</div>
  </div>
);

export default RapportZonesDetails;
